import { forwardRef } from 'react';
import { NavLink } from 'react-router-dom';
import { Icon, type IconName } from './ui/Icon';

interface NavItemSpec {
    key: string;
    label: string;
    to: string;
    icon: IconName;
}

const navItems: NavItemSpec[] = [
    { key: 'planet', label: 'Planet', to: '/planet', icon: 'planet' },
    { key: 'fleets', label: 'Fleets', to: '/fleets', icon: 'fleets' },
    { key: 'galaxy', label: 'Galaxy', to: '/galaxy', icon: 'galaxy' },
    { key: 'personal', label: 'Personal', to: '/personal', icon: 'personal' },
    { key: 'alliance', label: 'Alliance', to: '/alliance', icon: 'alliance' },
    { key: 'ranking', label: 'Ranking', to: '/ranking', icon: 'ranking' },
];

function NavItem({ icon, label, to }: Omit<NavItemSpec, 'key'>) {
    return (
        <NavLink
            to={to}
            className={({ isActive }) =>
                `flex flex-col items-center gap-1 px-4 py-1.5 rounded-tile focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hairline-strong ${isActive ? 'bg-white/[0.08] text-content-primary' : 'text-content-muted hover:text-content-secondary'
                }`
            }
        >
            <Icon name={icon} size={24} />
            <span className="text-label">{label}</span>
        </NavLink>
    );
}

/**
 * Floating glass dock replacing the old opaque `h-24 bg-space-900` bar.
 * `fixed`, so `AppShell` must pad the content area by this component's
 * measured height (see the `ref` this forwards) — mirrors `GameHUD`'s
 * own top-dock/measured-height pattern.
 *
 * Layering: fixed chrome sits at `z-40`, below the `z-50` overlay band. The
 * dock renders after `<Outlet />`, so at an equal z-index it would win on DOM
 * order and cover the bottom of every modal that is not portalled — and a
 * click meant for the modal would hit a nav link and navigate away.
 */
export const PlanetNavigation = forwardRef<HTMLElement>(function PlanetNavigation(_props, ref) {
    return (
        <nav
            ref={ref}
            data-tutorial-id="bottom-nav"
            className="fixed bottom-0 inset-x-0 z-40 flex justify-center py-3 bg-surface-hud backdrop-blur-hud border-t border-hairline"
            style={{ paddingBottom: 'max(0.75rem, env(safe-area-inset-bottom))' }}
        >
            <div className="w-full max-w-3xl mx-auto px-3 flex items-center justify-around lg:justify-center lg:gap-6">
                {navItems.map(({ key, ...item }) => (
                    <NavItem key={key} {...item} />
                ))}
            </div>
        </nav>
    );
});

export default PlanetNavigation;
